import React, { useMemo } from 'react';
import { Route, Switch, Redirect } from 'react-router-dom';
import { connect } from 'react-redux';
import { DEFAULT_ACTIVATE_ACCOUNT_OPTIONS, DEFAULT_DEVICE_LOGIN_OPTIONS, DEFAULT_FORGOT_PASSWORD_OPTIONS, DEFAULT_LOGIN_BY_CODE_OPTIONS, DEFAULT_LOGIN_OPTIONS, DEFAULT_REQUEST_DEVICE_CODE_OPTIONS, DEFAULT_REQUEST_LOGIN_CODE_OPTIONS, DEFAULT_RESET_PASSWORD_OPTIONS, DEFAULT_TWO_FACTOR_ENROLL_OPTIONS, DEFAULT_TWO_FACTOR_VERIFY_OPTIONS } from "./Constants.js";
import Login from "./Login.js";
import { createOnActivateAccount, createOnAfterEnroll, createOnBackToLogin, createOnForgotPasswordClick, createOnLogin, createOnLoginByCode, createOnLoginByCodeClick, createOnLoginWith2FA, createOnRequestDeviceAccessToken, createOnRequestDeviceCode, createOnConfirmUserCode, createOnRequestDeviceCodeClick, createOnRequestLoginCode, defaultAdjustPath } from "./Util.js";
import ForgotPassword from "./ForgotPassword.js";
import RequestLoginCode from "./RequestLoginCode.js";
import LoginByCode from "./LoginByCode.js";
import TwoFactorVerify from "./TwoFactorVerify.js";
import TwoFactorEnroll from "./TwoFactorEnroll.js";
import ActivateAccount from "./ActivateAccount.js";
import ResetPassword from "./ResetPassword.js";
import RequestDeviceCode from "./RequestDeviceCode.js";
import DeviceLogin from "./DeviceLogin.js";
import DeviceCodeSuccess from "./DeviceCodeSuccess.js";
function LoginRoutes(props) {
  const {
    adjustPath = defaultAdjustPath,
    redirectOnNotFound = true,
    activateAccountOpts,
    postLoginUrl,
    loginOpts,
    deviceLoginOpts,
    forgotPasswordOpts,
    requestLoginCodeOpts,
    requestDeviceCodeOpts,
    loginByCodeOpts,
    twoFactorVerifyOpts,
    twoFactorEnrollOpts,
    resetPasswordOpts,
    ...rest
  } = props;
  const onLoginOpts = useMemo(() => {
    return {
      login: {
        ...DEFAULT_LOGIN_OPTIONS,
        ...loginOpts
      },
      twoFactorVerify: {
        ...DEFAULT_TWO_FACTOR_VERIFY_OPTIONS,
        ...twoFactorVerifyOpts
      },
      twoFactorEnroll: {
        ...DEFAULT_TWO_FACTOR_ENROLL_OPTIONS,
        ...twoFactorEnrollOpts
      },
      postLoginUrl,
      adjustPath
    };
  }, [loginOpts, postLoginUrl, twoFactorVerifyOpts, twoFactorEnrollOpts, adjustPath]);
  const onBackToLogin = useMemo(() => {
    return createOnBackToLogin({
      login: {
        ...DEFAULT_LOGIN_OPTIONS,
        ...rest,
        ...loginOpts
      },
      deviceLogin: {
        ...DEFAULT_DEVICE_LOGIN_OPTIONS,
        ...deviceLoginOpts
      },
      adjustPath
    });
  }, [loginOpts, deviceLoginOpts, rest, adjustPath]);
  const forgotPassword = useMemo(() => {
    return {
      ...DEFAULT_FORGOT_PASSWORD_OPTIONS,
      onBackToLogin,
      ...rest,
      ...forgotPasswordOpts
    };
  }, [forgotPasswordOpts, onBackToLogin, rest]);
  const loginByCode = useMemo(() => {
    return {
      ...DEFAULT_LOGIN_BY_CODE_OPTIONS,
      onBackToLogin,
      onLogin: createOnLoginByCode({
        postLoginUrl,
        adjustPath
      }),
      ...rest,
      ...loginByCodeOpts
    };
  }, [loginByCodeOpts, onBackToLogin, postLoginUrl, adjustPath, rest]);
  const requestLoginCode = useMemo(() => {
    const opts = {
      ...DEFAULT_REQUEST_LOGIN_CODE_OPTIONS,
      onBackToLogin,
      onRequestLoginCode: createOnRequestLoginCode({
        loginByCode,
        adjustPath
      }),
      ...rest,
      ...requestLoginCodeOpts
    };
    return opts;
  }, [requestLoginCodeOpts, loginByCode, onBackToLogin, adjustPath, rest]);
  const requestDeviceCode = useMemo(() => {
    return {
      ...DEFAULT_REQUEST_DEVICE_CODE_OPTIONS,
      onRequestDeviceCode: createOnRequestDeviceCode({
        clientId: requestDeviceCodeOpts?.clientId
      }),
      onRequestDeviceAccessToken: createOnRequestDeviceAccessToken({
        clientId: requestDeviceCodeOpts?.clientId,
        adjustPath,
        postLoginUrl
      }),
      onBackToLogin,
      ...rest,
      ...requestDeviceCodeOpts
    };
  }, [requestDeviceCodeOpts, adjustPath, rest, onBackToLogin, postLoginUrl]);
  const login = useMemo(() => {
    return {
      ...DEFAULT_LOGIN_OPTIONS,
      onLogin: createOnLogin(onLoginOpts),
      onLoginByCodeClick: createOnLoginByCodeClick({
        requestLoginCode,
        adjustPath
      }),
      onRequestDeviceCodeClick: createOnRequestDeviceCodeClick({
        requestDeviceCode,
        adjustPath
      }),
      onForgotPasswordClick: createOnForgotPasswordClick({
        forgotPassword,
        adjustPath
      }),
      ...rest,
      ...loginOpts
    };
  }, [loginOpts, onLoginOpts, requestLoginCode, forgotPassword, adjustPath, requestDeviceCode, rest]);
  const deviceLogin = useMemo(() => {
    return {
      ...login,
      path: '/login/device',
      onLogin: createOnLogin({
        ...onLoginOpts,
        postLoginUrl: '/login/device/success'
      }),
      onConfirmUserCode: createOnConfirmUserCode({
        clientId: requestDeviceCodeOpts?.clientId
      }),
      options: {
        requestDeviceCodeEnabled: false
      }
    };
  }, [login, onLoginOpts, requestDeviceCodeOpts?.clientId]);
  const twoFactorVerify = useMemo(() => {
    return {
      ...DEFAULT_TWO_FACTOR_VERIFY_OPTIONS,
      onBackToLogin,
      onLogin: createOnLoginWith2FA({
        postLoginUrl,
        adjustPath
      }),
      ...rest,
      ...twoFactorVerifyOpts
    };
  }, [twoFactorVerifyOpts, onBackToLogin, postLoginUrl, adjustPath, rest]);
  const twoFactorEnroll = useMemo(() => {
    return {
      ...DEFAULT_TWO_FACTOR_ENROLL_OPTIONS,
      onBackToLogin,
      onAfterEnroll: createOnAfterEnroll({
        postLoginUrl,
        adjustPath
      }),
      ...rest,
      ...twoFactorEnrollOpts
    };
  }, [twoFactorEnrollOpts, onBackToLogin, postLoginUrl, adjustPath, rest]);
  const activateAccount = useMemo(() => {
    return {
      ...DEFAULT_ACTIVATE_ACCOUNT_OPTIONS,
      onBackToLogin,
      onLogin: createOnLogin(onLoginOpts),
      onActivateAccount: createOnActivateAccount({
        postLoginUrl,
        adjustPath
      }),
      ...rest,
      ...activateAccountOpts
    };
  }, [rest, activateAccountOpts, onBackToLogin, postLoginUrl, adjustPath, onLoginOpts]);
  const resetPassword = useMemo(() => {
    return {
      ...DEFAULT_RESET_PASSWORD_OPTIONS,
      ...rest,
      ...resetPasswordOpts,
      onBackToLogin
    };
  }, [rest, resetPasswordOpts, onBackToLogin]);
  return /*#__PURE__*/React.createElement(Switch, null, /*#__PURE__*/React.createElement(Route, {
    key: "forgotPassword",
    exact: true,
    path: adjustPath(forgotPassword.path || '/forgotPassword'),
    render: () => {
      return /*#__PURE__*/React.createElement(ForgotPassword, forgotPassword);
    }
  }), /*#__PURE__*/React.createElement(Route, {
    key: "requestLoginCode",
    exact: true,
    path: adjustPath(requestLoginCode.path || '/requestLoginCode'),
    render: () => {
      return /*#__PURE__*/React.createElement(RequestLoginCode, requestLoginCode);
    }
  }), /*#__PURE__*/React.createElement(Route, {
    key: "requestDeviceCode",
    exact: true,
    path: adjustPath(requestDeviceCode.path || '/requestDeviceCode'),
    render: () => {
      return /*#__PURE__*/React.createElement(RequestDeviceCode, requestDeviceCode);
    }
  }), /*#__PURE__*/React.createElement(Route, {
    key: "loginByCode",
    exact: true,
    path: adjustPath(loginByCode.path || '/loginByCode'),
    render: () => {
      return /*#__PURE__*/React.createElement(LoginByCode, loginByCode);
    }
  }), /*#__PURE__*/React.createElement(Route, {
    key: "twoFactorVerify",
    exact: true,
    path: adjustPath(twoFactorVerify.path || '/twoFactorVerify'),
    render: () => {
      return /*#__PURE__*/React.createElement(TwoFactorVerify, twoFactorVerify);
    }
  }), /*#__PURE__*/React.createElement(Route, {
    key: "twoFactorEnroll",
    exact: true,
    path: adjustPath(twoFactorEnroll.path || '/twoFactorEnroll'),
    render: () => {
      return /*#__PURE__*/React.createElement(TwoFactorEnroll, twoFactorEnroll);
    }
  }), /*#__PURE__*/React.createElement(Route, {
    key: "activateAccount",
    exact: true,
    path: adjustPath(activateAccount.path || '/activateAccount'),
    render: () => {
      return /*#__PURE__*/React.createElement(ActivateAccount, activateAccount);
    }
  }), /*#__PURE__*/React.createElement(Route, {
    key: "resetPassword",
    exact: true,
    path: adjustPath(resetPassword.path || '/resetPassword'),
    render: () => {
      return /*#__PURE__*/React.createElement(ResetPassword, resetPassword);
    }
  }), /*#__PURE__*/React.createElement(Route, {
    key: "login",
    exact: true,
    path: [adjustPath(login.path || '/login'), '/'],
    render: props => {
      return /*#__PURE__*/React.createElement(Login, login);
    }
  }), /*#__PURE__*/React.createElement(Route, {
    key: "deviceLogin",
    exact: true,
    path: adjustPath(deviceLogin.path || '/login/device'),
    render: props => {
      return /*#__PURE__*/React.createElement(DeviceLogin, deviceLogin);
    }
  }), /*#__PURE__*/React.createElement(Route, {
    key: "deviceLoginSuccess",
    exact: true,
    path: adjustPath(deviceLogin.successPath || '/login/device/success'),
    render: props => {
      return /*#__PURE__*/React.createElement(DeviceCodeSuccess, props);
    }
  }), redirectOnNotFound && /*#__PURE__*/React.createElement(Route, null, /*#__PURE__*/React.createElement(Redirect, {
    to: "/"
  })));
}
export default connect((state, props) => {
  return {
    router: props.router || state.router
  };
})(LoginRoutes);